import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import {
  UpdateContactDetailsAPIRequestData,
  UpdateCorrespondenceAddressAPIRequestData,
} from '@domgen/common-person-types';
import {
  AnalyticsService,
  Api,
  AppConfig,
  CardPaymentRedirectRequest,
  CardPaymentRedirectResponse,
  FeatureFlagService,
  LocalStorageService,
} from '@domgen/dgx-components';
import { CONFIG } from '@domgen/dgx-fe-config';
import { addDays, endOfWeek, format, isAfter, startOfWeek } from 'date-fns';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, pluck, switchMap, tap } from 'rxjs/operators';
import { PutNewAppointmentType } from '../+state/claim.actions';
import { FoundationModelSummary } from '../interfaces/foundation-model-summary';
import { UserService } from './user.service';

enum OrbitMethod {
  START_TRANSACTION = 'StartTransaction',
  GET_MANDATORY_DATA = 'GetMandatoryData',
  UPDATE_TRANSACTION = 'UpdateTransaction',
  GET_DATA = 'GetData',
  GET_QUESTION = 'GetQuestion',
  PUT_ANSWER = 'PutAnswer',
  PUT_NEW_CLAIM = 'PutNewClaim',
  GET_SERVICE_OPTION = 'GetServiceOption',
  GET_TRANSACTION = 'GetTransaction', // todo: No usages found (other than this file)
  PUT_SERVICE_OPTION = 'PutServiceOption',
  PAYMENT = 'Payment',
  PUT_REPAIR_DATA = 'PutRepairData',
}

enum ClaimsEndpoint {
  bookClaim = '/book-claim',
  superCategory = '/get-super-category',
  rebookSlots = '/rebook-engineer-slots',
  rebookRepair = '/rebook-engineer',
  getProductAttributes = '/get-product-attributes',
  bundleInspect = '/inspect-bundle',
  getNewAppointment = '/get-new-appointment',
  putNewAppointment = '/put-new-appointment',
  startBooking = '/start-booking',
  notification = '/notification',
}

enum PersonServiceEndpoint {
  updateAddress = '/update-correspondence-address',
  updateDetails = '/update-contact-details',
}

enum CallbackServiceEndpoint {
  callback = '/callback',
}

enum SmartFixEndPoint {
  startBoilerSession = 'smart-fix/start',
  deleteBoilerSession = 'smart-fix/end',
  sendBoilerResponse = 'smart-fix/send-response',
  initiateBedrockChat = 'smart-fix/initiate-bedrock-chat',
  initiateBedrockChatOptimized = 'smart-fix/initiate-bedrock-chat-optimized',
  sendBedrockChatMessage = 'smart-fix/send-bedrock-chat-message',
  sendBedrockChatMessageWithImage = 'smart-fix/send-bedrock-chat-message-with-image',
  sendBedrockChatMessageOptimized = 'smart-fix/send-bedrock-chat-message-optimized',
  listFoundationModels = 'smart-fix/list-foundation-models',
  markBedrockChatComplete = 'smart-fix/mark-bedrock-chat-complete',
  addBedrockChatFeedback = 'smart-fix/add-bedrock-chat-feedback',
  gatherContextFromUser = 'smart-fix/gather-context-from-user',
  startAgentConversation = 'smart-fix/start-agent-conversation',
  continueAgentConversation = 'smart-fix/continue-agent-conversation',
}

enum SmartFixEndPointChatWithDocs {
  initiateBedrockChat = 'smart-fix/initiate-bedrock-chat-CWD',
  sendBedrockChatMessage = 'smart-fix/send-bedrock-chat-message-CWD',
  listFoundationModels = 'smart-fix/list-foundation-models-CWD',
}

export type contextQuestions = {
  applianceType: string;
  errorDescription: string;
  firstSeen: string;
  stepsTried: string;
};

export interface AgentResponse {
  sessionId: string;
  response: string;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    @Inject(CONFIG) private readonly config: AppConfig,
    private http: HttpClient,
    private user: UserService,
    private localStorage: LocalStorageService,
    private analyticsService: AnalyticsService,
    private readonly featureFlagService: FeatureFlagService
  ) {}

  getGenericModelNumber(modelApplianceCombination: string) {
    return this.config.genericModelNumber[modelApplianceCombination];
  }

  getApiUrl(endpoint: ClaimsEndpoint) {
    return `${this.config.api}${endpoint}`;
  }

  getApiUrlv2(endpoint: ClaimsEndpoint) {
    // for /notication endpoint which uses v2
    return `${this.config.apiv2}${endpoint}`;
  }

  getPersonServiceUrl(endpoint: PersonServiceEndpoint) {
    return `${this.config.commonPersonServiceUrl}${endpoint}`;
  }

  getCallbackUrl(endpoint: CallbackServiceEndpoint) {
    return `${this.config.api}${endpoint}`;
  }

  getSmartFixApiUrl(endpoint: SmartFixEndPoint) {
    return `${this.config.smartFixApi}${endpoint}`;
  }

  getSmartFixApiUrlChatWithDocs(endpoint: SmartFixEndPointChatWithDocs) {
    return `${this.config.smartFixApi}${endpoint}`;
  }

  getBVModelNumber(request: any) {
    const url = `${this.config.brightClaimsAPI}/api/v1/model-number-lookup`;

    return this.http.post<{ value: string; label: string; category: string }[]>(
      url,
      request
    );
  }

  listBedrockModelsBeth(): Observable<FoundationModelSummary[]> {
    return this.http
      .post<FoundationModelSummary[]>(
        `${this.getSmartFixApiUrlChatWithDocs(
          SmartFixEndPointChatWithDocs.listFoundationModels
        )}`,
        {}
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  initiateBedrockChatChatWithDocs(
    text: string,
    productType?: string,
    planNumber?: string,
    prompt?: string,
    modelArn?: string
  ): Observable<Api.BedrockSession> {
    const { enablePromptEditingForSmartFix } = this.config.runtimeFeatures;
    return this.http
      .post<Api.BedrockSession>(
        `${this.getSmartFixApiUrlChatWithDocs(
          SmartFixEndPointChatWithDocs.initiateBedrockChat
        )}`,
        enablePromptEditingForSmartFix
          ? {
              text,
              systemPrompt: prompt,
              modelArn: modelArn,
              productType,
              planNumber,
            }
          : {
              text,
              productType,
              planNumber,
            }
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  sendBedrockChatMessageChatWithDocs(
    sessionId: string,
    text: string,
    prompt?: string,
    modelArn?: string
  ): Observable<Api.BedrockSession> {
    const { enablePromptEditingForSmartFix } = this.config.runtimeFeatures;
    return this.http
      .post<Api.BedrockSession>(
        `${this.getSmartFixApiUrlChatWithDocs(
          SmartFixEndPointChatWithDocs.sendBedrockChatMessage
        )}`,
        enablePromptEditingForSmartFix
          ? {
              text,
              systemPrompt: prompt,
              modelArn: modelArn,
              sessionId,
            }
          : {
              text,
              sessionId,
            }
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  startBoilerSession(text: string): Observable<Api.LexStartBoilerSession> {
    return this.http
      .post<Api.LexStartBoilerSession>(
        `${this.getSmartFixApiUrl(SmartFixEndPoint.startBoilerSession)}`,
        {
          text,
        }
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  sendBedrockChatMessageWithImage(
    text: string,
    image: { base64: string },
    prompt?: string,
    modelArn?: string
  ): Observable<Api.BedrockImageAnalysisResponse> {
    const { enablePromptEditingForSmartFix } = this.config.runtimeFeatures;

    const payload = enablePromptEditingForSmartFix
      ? {
          text,
          systemPrompt: prompt,
          modelArn: modelArn,
          image,
        }
      : {
          text,
          image,
        };

    console.log(payload);
    return this.http
      .post<Api.BedrockImageAnalysisResponse>(
        `${this.getSmartFixApiUrl(
          SmartFixEndPoint.sendBedrockChatMessageWithImage
        )}`,
        payload
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  sendBoilerResponse(
    sessionId: string,
    text: string
  ): Observable<Api.LexBoilerSession> {
    return this.http
      .post<Api.LexBoilerSession>(
        `${this.getSmartFixApiUrl(SmartFixEndPoint.sendBoilerResponse)}`,
        {
          text,
          sessionId,
        }
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  listBedrockModels(): Observable<FoundationModelSummary[]> {
    return this.http
      .post<FoundationModelSummary[]>(
        `${this.getSmartFixApiUrl(SmartFixEndPoint.listFoundationModels)}`,
        {}
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  startAgentConversation(
    summary: string,
    brand?: string,
    modelNumber?: string,
    productType?: string,
    planNumber?: string
  ): Observable<AgentResponse> {
    console.log('summary receiveed', summary);
    const url = this.getSmartFixApiUrl(SmartFixEndPoint.startAgentConversation);
    return this.http
      .post<AgentResponse>(url, {
        headers: { 'Content-Type': 'application/json' },
        summary,
        brand,
        modelNumber,
        productType,
        planNumber,
      })
      .pipe(catchError(this.handleSmartFixError));
  }

  continueAgentConversation(sessionId: string, text: string): Observable<any> {
    const url = this.getSmartFixApiUrl(
      SmartFixEndPoint.continueAgentConversation
    );
    return this.http.post<{
      text: string;
      sessionId: string;
    }>(url, {
      sessionId,
      text,
      headers: { 'Content-Type': 'application/json' },
    });
  }

  initiateBedrockChat(
    text: string,
    productType?: string,
    planNumber?: string,
    prompt?: string,
    modelArn?: string,
    summary?: string
  ): Observable<Api.BedrockSession> {
    const { enablePromptEditingForSmartFix } = this.config.runtimeFeatures;
    const endpoint = this.featureFlagService.isFeatureFlagEnabled(
      'bedrock-rag-optimization'
    )
      ? SmartFixEndPoint.initiateBedrockChatOptimized
      : SmartFixEndPoint.initiateBedrockChat;
    return this.http
      .post<Api.BedrockSession>(
        `${this.getSmartFixApiUrl(endpoint)}`,
        enablePromptEditingForSmartFix
          ? {
              text,
              systemPrompt: prompt,
              modelArn: modelArn,
              productType,
              planNumber,
              summary,
            }
          : {
              text,
              productType,
              planNumber,
              summary,
            }
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  gatherContextFromUser(payload: {
    answers: string[];
    answer?: string;
    productType?: string;
    brand?: string;
    modelNumer?: string;
  }) {
    const url = this.getSmartFixApiUrl(SmartFixEndPoint.gatherContextFromUser);
    return this.http
      .post<{
        done: boolean;
        answers: string[];
        nextQuestion?: string;
        reAsk?: string;
        summary?: string;
        greeting?: string;
      }>(url, payload)
      .pipe(catchError(this.handleSmartFixError));
  }

  sendBedrockChatMessage(
    sessionId: string,
    text: string,
    prompt?: string,
    modelArn?: string
  ): Observable<Api.BedrockSession> {
    const { enablePromptEditingForSmartFix } = this.config.runtimeFeatures;
    const endpoint = this.featureFlagService.isFeatureFlagEnabled(
      'bedrock-rag-optimization'
    )
      ? SmartFixEndPoint.sendBedrockChatMessageOptimized
      : SmartFixEndPoint.sendBedrockChatMessage;
    return this.http
      .post<Api.BedrockSession>(
        `${this.getSmartFixApiUrl(endpoint)}`,
        enablePromptEditingForSmartFix
          ? {
              text,
              systemPrompt: prompt,
              modelArn: modelArn,
              sessionId,
            }
          : {
              text,
              sessionId,
            }
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  markBedrockChatComplete(sessionId: string): Observable<Api.BedrockSession> {
    return this.http
      .post<any>(
        `${this.getSmartFixApiUrl(SmartFixEndPoint.markBedrockChatComplete)}`,
        {
          sessionId,
        }
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  addBedrockChatFeedback(
    isFeedbackPositive: boolean,
    feedbackContent: string,
    rating: string,
    sessionId: string
  ): Observable<Api.BedrockSession> {
    return this.http
      .post<any>(
        `${this.getSmartFixApiUrl(SmartFixEndPoint.addBedrockChatFeedback)}`,
        {
          isPositive: isFeedbackPositive,
          content: feedbackContent,
          sessionId,
          rating,
        }
      )
      .pipe(catchError(this.handleSmartFixError));
  }

  handleError(error: HttpErrorResponse) {
    let errMsg = error?.error?.message;
    switch (true) {
      case !!error.error?.message:
        // A client-side or network error occurred. Handle it accordingly.
        error.error.message = `${error.status} ${error.error?.message}`;
        console.error('An error occurred:', error.error.message);
        return throwError({
          ...error.error,
          status: error.status,
          name: errMsg,
        });

      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,

      case typeof error.error === 'string':
        errMsg = `${error.status} ${error.error}`;
        console.error('An error occurred:', errMsg);
        return throwError({
          name: 'Api Error',
          message: errMsg,
          errorCode: error.status,
        });

      case !!error.error?.ErrorCode:
        errMsg = `${error.status} Orbit code: ${error.error.ErrorCode}`;
        console.error('An error occurred:', errMsg);
        return throwError({
          name: 'Orbit Error',
          status: error?.status,
          message: errMsg,
          errorCode: error.error.ErrorCode,
          errorDetail: error?.error?.ErrorDetail,
        });

      default:
        console.error(`Backend returned code ${error.status}`);
        return throwError(error);
    }
  }

  getSuperCategory(
    applianceCode: string
  ): Observable<Api.GetSuperCategoryResponse> {
    return this.http
      .post<Api.Response<Api.GetSuperCategoryResponse>>(
        `${this.getApiUrl(ClaimsEndpoint.superCategory)}`,
        {
          applianceCode,
        }
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          return throwError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse);
        }),
        pluck('result')
      );
  }

  startClaimBooking() {
    return this.http
      .post<Api.Response<Api.StartBookingResponse>>(
        this.getApiUrl(ClaimsEndpoint.startBooking),
        {
          channelCode: this.config?.channelCode,
          countryCode: this.config?.countryCode,
        },
        { observe: 'response' }
      )
      .pipe(
        switchMap((response) => {
          this.user.correlationID = response.headers.get('x-correlation-id');
          const result = response.body?.result;
          const modelNumberIsOption =
            result?.mandatoryData?.OEMModelDataRequired === false;
          const hasModelNumber =
            (result?.mandatoryData?.ModelNumber ?? '').length > 0;
          const hasNoBookingOEM =
            result?.mandatoryData?.BookingOEM === '' ||
            !result?.mandatoryData?.BookingOEM;
          const hasBVCategoryNumber =
            !!result?.mandatoryData?.BevaluedCategoryID;
          const requiresModelNumberCheck =
            hasNoBookingOEM &&
            hasModelNumber &&
            modelNumberIsOption &&
            hasBVCategoryNumber;

          if (requiresModelNumberCheck) {
            return this.getBVModelNumber({
              modelNumber: result?.mandatoryData?.ModelNumber,
              group: result?.mandatoryData?.BevaluedCategoryID,
              manufacturer: result?.bundle?.manufacturer,
            }).pipe(
              map((bvLookupResponse) => {
                const modelNumberExistsInList = bvLookupResponse.find(
                  (i) => i.value === result?.mandatoryData?.ModelNumber
                );

                if (!modelNumberExistsInList) {
                  (response as any).body.result.mandatoryData[
                    'ModelNumberInvalid'
                  ] = true;
                }
                return response;
              }),
              catchError(() => {
                // If this API fails, just let the user carry on
                return of(response);
              })
            );
          }
          return of(response);
        }),
        catchError((err: HttpErrorResponse) => {
          return this.handleError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse);
        }),
        pluck('body', 'result'),
        catchError(this.handleError)
      );
  }

  getReflectData() {
    return this.http
      .post<Api.Response<Api.Reflect>>(
        `${this.getApiUrl(ClaimsEndpoint.bundleInspect)}`,
        {
          fields: [
            'asvOffered',
            'countryCode',
            'claimType',
            'journeyState',
            'applianceCode',
            'makerCode',
            'user',
            'homeTelephone',
            'addressLine1',
            'productType',
            'addressLine2',
            'addressLine3',
            'mobilePhone',
            'addressLine4',
            'postCode',
            'initials',
            'manufacturer',
            'planNumber',
            'customerTitle',
            'surname',
            'firstName',
            'email',
            'claimId',
            'workPhone',
            'rootURL',
            'myAccountURL',
            'personalDetailsPath',
            'dashboardPath',
            'logoutPath',
            'isRiskOwner',
            'decision',
            'crossSellReqSource',
            'crossSellReqAction',
            'store',
            'companyCode',
            'schemeCode',
            'journey',
            'userState',
          ],
        },
        { observe: 'response' }
      )
      .pipe(
        switchMap((response) => {
          this.user.correlationID = response.headers.get('x-correlation-id');
          return of(response);
        }),
        catchError((err: HttpErrorResponse) => {
          return this.handleError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse);
        }),
        pluck('body', 'result'),
        catchError(this.handleError)
      );
  }

  rebookServiceOption(
    claimID: string,
    availabilityStartDate?: string | Date,
    maximumDays?: number
  ): Observable<Api.PutServiceOptionResponse> {
    /* todo: this endpoint does not have the following properties in the spec:
        - ExtraAvailability
        - AvailabilityRequestDays
        - AvailabilityMaximumDays
        - AvailabilityStartDate
        - AvailabilityEndDate
        - AuthorityLimit
        - ManualReferralAllowed
        - ManualReferralServiceOptionID
        - PaymentData - Unused
     */

    let requestedPastMaximumDays = false;
    if (!availabilityStartDate) {
      availabilityStartDate = startOfWeek(new Date(), { weekStartsOn: 1 });
    }

    if (typeof availabilityStartDate === 'string') {
      availabilityStartDate = startOfWeek(new Date(availabilityStartDate), {
        weekStartsOn: 1,
      });
    }

    let endDate = endOfWeek(addDays(new Date(availabilityStartDate), 6), {
      weekStartsOn: 1,
    });

    if (maximumDays) {
      if (isAfter(endDate, addDays(new Date(), maximumDays - 1))) {
        endDate = addDays(new Date(), maximumDays - 1);
        requestedPastMaximumDays = true;
      }
    }

    const availabilityEndDate = format(endDate, 'yyyy-MM-dd');

    availabilityStartDate = format(availabilityStartDate, 'yyyy-MM-dd');

    return this.http
      .post<Api.Response<Api.PutServiceOptionResponse>>(
        `${this.getApiUrl(ClaimsEndpoint.getNewAppointment)}`,
        {
          claimID,
          availabilityStartDate,
          availabilityEndDate,
          channelCode: this.config?.channelCode,
          countryCode: this.config?.countryCode,
        }
      )
      .pipe(
        map((result) => {
          // Used to handle an Orbit error where all day slots extra availability does not work as expected. Therefore we set extraAvailability to true as long as the requestedEndDate is earlier than the maximum number of days in the future we can request
          result.result.ExtraAvailability = !requestedPastMaximumDays;
          return result;
        }),
        catchError((err: HttpErrorResponse) => {
          return this.handleError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse);
        }),
        pluck('result')
      );
  }

  putNewAppointment(
    payload: PutNewAppointmentType
  ): Observable<Api.PutRepairDataResponse> {
    return this.http
      .put<Api.Response<Api.PutNewAppointmentResponse>>(
        this.getApiUrl(ClaimsEndpoint.putNewAppointment),
        {
          ...payload,
          appointmentNotes: '',
          channelCode: this.config?.channelCode,
          countryCode: this.config?.countryCode,
        }
      )
      .pipe(
        map((response) => {
          return {
            status: response.status,
            result: {
              ClaimID: response.result.claimID,
              ManufacturerName: response.result.manufacturer,
              PlanNumber: response.result.planNumber,
              ServiceOption: response.result.serviceOption,
              AppointmentDate: response.result.appointmentDate,
              AppointmentSlot: response.result.appointmentSlot,
              ServiceProviderCompanyName:
                response.result.serviceProviderCompanyName,
              ServiceProviderBuildingName:
                response.result.serviceProviderBuilding,
              ServiceProviderHouseStreetName:
                response.result.serviceProviderHouseStreetName,
              ServiceProviderLocalArea:
                response.result.serviceProviderLocalArea,
              ServiceProviderTownCity: response.result.serviceProviderTownCity,
              ServiceProviderPostCode: response.result.serviceProviderPostCode,
              ServiceProviderTelephone: response.result.serviceProviderPhone,
              ServiceProviderEmail: response.result.serviceProviderPublicEmail,
            },
          } as Api.Response<Api.PutRepairDataResponse>;
        }),
        catchError((err: HttpErrorResponse) => {
          return this.handleError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse);
        }),
        pluck('result')
      );
  }

  rebookRepairOption(
    payload: Api.ReBookEngineer
  ): Observable<Api.PutRepairDataResponse> {
    /*
      todo: missing properties from response:
        - AuthorityLimit - Used
        - ClaimReference - Used
        - FaultSelected - Used
        - ClaimQuestionsArray - Used
        - AvailabilityData - Used
        - ServiceProviderHours - Used
        - SerialNumber - Possibly used?
        - ModelNumber - Possibly used?
        - AppointmentSlot - Unused
        - ContactAddressType - Unused
        - ContactBuilding - Unused
        - ContactEmail - Unused
        - ContactHouseStreetName - Unused
        - ContactLocalArea - Unused
        - ContactMobile - Unused
        - ContactName - Unused
        - ContactPostCode - Unused
        - ContactTownCity - Unused
        - FaultCategorySelected - Unused
        - ManufacturerName - Unused
        - ProductDescription - Unused
        - ProductGroup - Unused
        - ProductType - Unused
        - RepairSlot - Unused
        - ServiceProviderID - Unused
        - ServiceProviderWebsite - Unused
        - Status - Unused
        - StatusCode - Unused
        - StatusDebugging - Unused
     */
    return this.http
      .post<Api.Response<Api.PutRepairDataResponse>>(
        `${this.getApiUrl(ClaimsEndpoint.rebookRepair)}`,
        {
          ...payload,
          ChannelCode: this.config?.channelCode,
          CountryCode: this.config?.countryCode,
        }
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          return this.handleError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse);
        }),
        pluck('result')
      );
  }

  getProductAttributes(): Observable<Api.GetProductAttributesResponse> {
    /*
    todo: this is missing the following properties:
      - PolicyNumber
      - APIName
     */
    return this.http
      .post<Api.Response<Api.GetProductAttributesResponse>>(
        `${this.getApiUrl(ClaimsEndpoint.getProductAttributes)}`,
        {
          ChannelCode: this.config?.channelCode,
          CountryCode: this.config?.countryCode,
        }
      )
      .pipe(
        catchError((err: HttpErrorResponse) => {
          return this.handleError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse);
        }),
        pluck('result')
      );
  }

  //common person service methods

  updateContactDetails(
    payload: UpdateContactDetailsAPIRequestData
  ): Observable<{ status: string }> {
    return this.http
      .post<any>(
        `${this.getPersonServiceUrl(PersonServiceEndpoint.updateDetails)}`,
        payload
      )
      .pipe(
        catchError((err) =>
          throwError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse)
        ),
        pluck('result')
      );
  }

  updateContactAdress(
    payload: UpdateCorrespondenceAddressAPIRequestData
  ): Observable<{ status: string }> {
    return this.http
      .post<any>(
        `${this.getPersonServiceUrl(PersonServiceEndpoint.updateAddress)}`,
        payload
      )
      .pipe(
        catchError((err) =>
          throwError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse)
        ),
        pluck('result')
      );
  }

  // Orbit methods

  private makeOrbitRequest<R = unknown>(
    method: OrbitMethod,
    parameters: Api.OrbitParameters = {}
  ) {
    return this.localStorage.getItem('GUID').pipe(
      switchMap((guid) => {
        return this.http
          .post<Api.Response<R>>(
            `${this.getApiUrl(ClaimsEndpoint.bookClaim)}`,
            {
              method,
              parameters: {
                ...parameters,
                ChannelCode: this.config?.channelCode,
                CountryCode: this.config?.countryCode,
                ...(method === OrbitMethod.START_TRANSACTION
                  ? {}
                  : {
                      GUID: guid,
                    }),
              },
            }
          )
          .pipe(
            catchError((err: HttpErrorResponse) => {
              const errorDescription =
                err?.error?.ErrorDetail?.StatusDebugging?.Description ?? '';

              // Convert RD013 with a specific error message as slot taken
              if (
                err.error.ErrorCode === 'RD013' &&
                errorDescription.includes(
                  'cannot book slot for service appointment'
                )
              ) {
                return this.handleError({
                  status: err.status,
                  error: {
                    ...err.error,
                    ErrorCode: 'RD001',
                  },
                } as HttpErrorResponse);
              }

              return this.handleError({
                status: err.status,
                error: err.error,
              } as HttpErrorResponse);
            }),
            pluck('result')
          );
      })
    );
  }

  startTransaction(
    payload: Api.StartTransactionRequest
  ): Observable<Api.StartTransactionResponse> {
    return this.makeOrbitRequest<Api.StartTransactionResponse>(
      OrbitMethod.START_TRANSACTION,
      payload
    );
  }

  getMandatoryData(
    planNumber: string,
    manufacturer: string
  ): Observable<Api.GetMandatoryDataResponse> {
    return this.makeOrbitRequest<Api.GetMandatoryDataResponse>(
      OrbitMethod.GET_MANDATORY_DATA,
      { planNumber: planNumber }
    ).pipe(
      switchMap((response) => {
        const result = response;
        const modelNumberIsOption = result.OEMModelDataRequired === false;
        const hasModelNumber = (result?.ModelNumber ?? '').length > 0;
        const hasNoBookingOEM =
          result?.BookingOEM === '' || !result?.BookingOEM;
        const hasBVCategoryNumber = !!result?.BevaluedCategoryID;
        const requiresModelNumberCheck =
          hasNoBookingOEM &&
          hasModelNumber &&
          modelNumberIsOption &&
          hasBVCategoryNumber;

        if (requiresModelNumberCheck) {
          return this.getBVModelNumber({
            modelNumber: result?.ModelNumber,
            group: result?.BevaluedCategoryID,
            manufacturer,
          }).pipe(
            map((bvLookupResponse) => {
              const modelNumberExistsInList = bvLookupResponse.find(
                (i) => i.value === result?.ModelNumber
              );

              if (!modelNumberExistsInList) {
                response['ModelNumberInvalid'] = true;
              }
              return response;
            }),
            catchError(() => {
              // If this API fails, just let the user carry on
              return of(response);
            })
          );
        }

        return of(response);
      })
    );
  }

  getData(payload: Api.GetDataRequest): Observable<Api.GetDataResponse> {
    return this.makeOrbitRequest<Api.GetDataResponse>(
      OrbitMethod.GET_DATA,
      payload
    );
  }

  getQuestion(payload: Api.GetQuestionRequest) {
    return this.makeOrbitRequest<Api.GetQuestionResponse>(
      OrbitMethod.GET_QUESTION,
      payload
    );
  }

  putAnswer(payload: Api.PutAnswerRequest) {
    return this.makeOrbitRequest<Api.PutAnswerResponse>(
      OrbitMethod.PUT_ANSWER,
      payload
    );
  }

  updateTransaction(payload: Api.UpdateTransactionRequest) {
    return this.makeOrbitRequest<Api.UpdateTransactionResponse>(
      OrbitMethod.UPDATE_TRANSACTION,
      payload
    );
  }

  putNewClaim(payload: Api.PutNewClaimRequest) {
    return this.makeOrbitRequest<Api.PutNewClaimResponse>(
      OrbitMethod.PUT_NEW_CLAIM,
      payload
    );
  }

  getServiceOptions(
    payload: Api.GetServiceOptionRequest
  ): Observable<Api.GetServiceOptionResponse> {
    return this.makeOrbitRequest<Api.GetServiceOptionResponse>(
      OrbitMethod.GET_SERVICE_OPTION,
      payload
    );
  }

  //todo: unused method
  getTransaction(): Observable<Api.GetTransactionResponse> {
    return this.makeOrbitRequest<Api.GetTransactionResponse>(
      OrbitMethod.GET_TRANSACTION
    );
  }

  putServiceOption(
    payload: Api.PutServiceOptionRequest
  ): Observable<Api.PutServiceOptionResponse> {
    return this.makeOrbitRequest<Api.PutServiceOptionResponse>(
      OrbitMethod.PUT_SERVICE_OPTION,
      payload
    );
  }

  putRepairData(
    payload: Api.PutRepairDataRequest
  ): Observable<Api.PutRepairDataResponse> {
    return this.makeOrbitRequest<Api.PutRepairDataResponse>(
      OrbitMethod.PUT_REPAIR_DATA,
      payload
    );
  }

  callbackComplete(payload: Api.CallbackRequest) {
    return this.http
      .post<any>(
        `${this.getCallbackUrl(CallbackServiceEndpoint.callback)}`,
        payload
      )
      .pipe(
        catchError((err) =>
          throwError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse)
        ),
        pluck('result')
      );
  }

  postCardPaymentRedirect(
    p: CardPaymentRedirectRequest
  ): Observable<CardPaymentRedirectResponse> {
    return this.http
      .post<any>(`${this.config.common}/v2/card/payment-redirect`, p)
      .pipe(
        catchError((err) =>
          throwError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse)
        ),
        pluck('result')
      );
  }

  sendVulnerableCustomersFormAnswers(
    p: Api.VulnerableCustomerQuestionsAnswersRequest
  ): Observable<Api.VulnerableCustomerQuestionsAnswersResponse> {
    return this.http
      .post<any>(`${this.config.brightClaimsAPI}/api/v1/notification`, p)
      .pipe(
        catchError((err) =>
          throwError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse)
        )
      );
  }

  completePayment(
    payload: Api.PaymentRequest
  ): Observable<Api.PaymentResponse> {
    return this.http.post<any>(`${this.config.api}/payment`, payload).pipe(
      catchError((err) =>
        throwError({
          status: err.status,
          error: err.error,
        } as HttpErrorResponse)
      ),
      pluck('result')
    );
  }

  setPassword(payload: Api.PasswordRequest): Observable<Api.PasswordResponse> {
    return this.http
      .post<any>(
        `${this.config.myAccountIdentity}/v2/confirm/password`,
        payload
      )
      .pipe(
        catchError((err) =>
          throwError({
            status: err.status,
            error: err.error,
          } as HttpErrorResponse)
        )
      );
  }

  searchFault(
    request: {
      description: string;
      lineId: string;
    },
    faultData: Api.FaultData | Api.FaultData[]
  ): Observable<Api.FaultSearchResponse[]> {
    const errorData = Array.isArray(faultData) ? faultData : [faultData];
    const url = `${this.config.api}/fault-search`;
    return this.http
      .post<Api.Response<{ items: Api.FaultSearchResponse[] }>>(url, {
        ...request,
        numberOfResults: 50,
      })
      .pipe(
        map((value) => {
          const allCategoriesFound = value.result.items.every((item) => {
            return errorData.find(
              (i) => i.FaultCategoryName === item.faultId_lv2
            );
          });

          if (!allCategoriesFound) {
            this.analyticsService.trigger('FTS.NoMatchingResults', {
              ...request,
              returnedCategories: value.result.items.map(
                (item) => `${item.faultId_lv2} - ${item.problemId_lv3}`
              ),
              faultData: errorData.map((data) => data.FaultCategoryName),
            });

            throw new Error('Not valid');
          }

          return value.result.items;
        })
      );
  }

  handleSmartFixError(error: HttpErrorResponse) {
    return throwError(() => new Error('New error smart fix'));
  }
}
